/**
 * RegistrationTabs — the Registration step's bottom tab control, faithful to
 * the legacy XAF detail view and the approved prototype:
 *
 *   Received Dates · Attachments · Sample Condition Check · Custom Request ·
 *   Notes · Item Charge · Image Edit
 *
 * Each tab is a shared <AppDataGrid> plus its own New / Edit / Delete popup
 * (the shared <AppDialog>).  Grid styling, per-column filters, pagination and
 * the footer all come from AppDataGrid — nothing is hand-rolled here.
 *
 * A Job ID can receive samples MULTIPLE times: Received Dates is the list of
 * receipt events, and both Attachments and Sample Condition Checks link to one
 * receipt through its Received Date.
 *
 * TODO(AI-29131): every mutation below updates local state only.  Wire each to
 * its API when the child-collection endpoints land — the record shapes here are
 * the intended payloads.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { AppToolbar } from '../../../components/AppToolbar';
import { fmtLocalDate, fmtLocalDateTime } from '../../../utils/datetime';
import { LookupCombo } from './LookupCombo';
import { api } from '../../../api/client';
import {
  ATT_CAT_LABELS, ATT_CAT_OPTIONS, ATT_CAT_VALUES,
  SCC_POINTS, nextKey, resolveDueDate, toDateInput, toDateTimeInput,
  type IcPricingOption, type SrAttachment, type SrHeader, type SrImage, type SrItemCharge, type SrNote,
  type SrLookup, type SrReceivedDate, type SrSccComment, type SrSccRecord, type SrTabState, type SrTest,
} from './srData';

const TAB_LABELS = [
  'Received Dates', 'Attachments', 'Sample Condition Check', 'Custom Request',
  'Notes', 'Item Charge', 'Image Edit',
] as const;

type TabIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface RegistrationTabsProps {
  header: SrHeader;
  jobId: string;
  /** The current user (Created By / Operator / Author). */
  user: string;
  /** Server-local "now" the form stamps new child records with. */
  now: string;
  tabs: SrTabState;
  setTabs: (next: SrTabState) => void;
  /** Header-level test template — the Custom Request tab's rows. */
  templateTests: SrTest[];
  /** Per-test due-date override + hold + TAT, keyed by test name. */
  customRequest: Record<string, { hold: boolean; due: string; tat: string }>;
  setCustomRequest: (next: Record<string, { hold: boolean; due: string; tat: string }>) => void;
  /** TAT name → hours map (from rule data). Used for per-test DueDate recalc. */
  tatHoursMap: Record<string, number>;
  /** Holiday date strings (YYYY-MM-DD) — used by resolveDueDate. */
  holidaySet: ReadonlySet<string>;
  /** 'Sampling' | 'Environmental' etc. — passed to resolveDueDate. */
  registrationType: string;
  /** Lookup catalogs for dropdown fields in child dialogs. */
  lookups: Record<string, SrLookup>;
  onWarn: (message: string) => void;
  onInfo: (message: string) => void;
  onSuccess: (message: string) => void;
  /** Shared confirm for every state-changing tab action (standing rule 7). */
  confirmDelete: (what: string, run: () => void) => void;
}

export function RegistrationTabs(props: RegistrationTabsProps) {
  const [tab, setTab] = useState<TabIndex>(0);

  return (
    <section className="sr-sec">
      <div className="sr-sec-b" style={{ paddingBottom: 6 }}>
        <div className="sr-tabbar" role="tablist">
          {TAB_LABELS.map((label, i) => (
            <button
              key={label}
              role="tab"
              aria-selected={tab === i}
              className={`sr-tb${tab === i ? ' active' : ''}`}
              onClick={() => setTab(i as TabIndex)}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={{ minHeight: 90 }}>
          {tab === 0 && <ReceivedDatesTab {...props} />}
          {tab === 1 && <AttachmentsTab {...props} />}
          {tab === 2 && <SccTab {...props} />}
          {tab === 3 && <CustomRequestTab {...props} />}
          {tab === 4 && <NotesTab {...props} />}
          {tab === 5 && <ItemChargeTab {...props} />}
          {tab === 6 && <ImageEditTab {...props} />}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Received Dates — one row per receipt event
// ─────────────────────────────────────────────────────────────────────────────
function ReceivedDatesTab({
  header, user, now, tabs, setTabs, lookups, onWarn, confirmDelete,
}: RegistrationTabsProps) {
  const [editing, setEditing] = useState<SrReceivedDate | null>(null);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState<Set<string | number>>(new Set());

  const blank = (): SrReceivedDate => ({
    key: nextKey('rd'), oid: '', date: now, by: user, relinq: header.relinquishedBy.join('; '), n: 1,
    matrix: [...header.matrices], tat: header.tat, cooler: header.coolerId,
    cat: [...header.sampleCategory], createdBy: user, created: now,
  });

  const save = (rec: SrReceivedDate) => {
    if (!rec.date) { onWarn('Enter the Received Date and time for this receipt.'); return; }
    const exists = tabs.receivedDates.some(r => r.key === rec.key);
    setTabs({
      ...tabs,
      receivedDates: exists
        ? tabs.receivedDates.map(r => (r.key === rec.key ? rec : r))
        : [...tabs.receivedDates, rec],
    });
    setOpen(false);
  };

  const cols: AppColumn<SrReceivedDate>[] = [
    editCol<SrReceivedDate>(r => { setEditing(r); setOpen(true); }),
    { field: 'date', header: 'ReceivedDate', width: 150, text: r => fmtLocalDateTime(r.date) },
    { field: 'by', header: 'ReceivedBy', width: 110 },
    { field: 'relinq', header: 'RelinquishedBy', width: 130 },
    { field: 'n', header: 'NoOfSamples', type: 'number', width: 105 },
    { field: 'matrix', header: 'SampleMatrix', width: 130, text: r => r.matrix.join('; ') },
    { field: 'tat', header: 'TAT', width: 90 },
    { field: 'cooler', header: 'CoolerID', width: 100 },
    { field: 'cat', header: 'SampleCategory', width: 130, text: r => r.cat.join('; ') },
    { field: 'createdBy', header: 'CreatedBy', width: 105 },
    { field: 'created', header: 'CreatedDate', width: 120, text: r => fmtLocalDate(r.created) },
  ];

  return (
    <>
      <AppToolbar
        onNew={() => { setEditing(blank()); setOpen(true); }}
        onEdit={() => {
          const rec = tabs.receivedDates.find(r => sel.has(r.key));
          if (rec) { setEditing({ ...rec }); setOpen(true); }
        }}
        editDisabled={sel.size !== 1}
        onDelete={() => confirmDelete(`${sel.size} received date(s)`, () => {
          setTabs({ ...tabs, receivedDates: tabs.receivedDates.filter(r => !sel.has(r.key)) });
          setSel(new Set());
        })}
        deleteDisabled={sel.size === 0}
      />
      <AppDataGrid<SrReceivedDate>
        rows={tabs.receivedDates}
        columns={cols}
        getRowId={r => r.key}
        selectable
        selectedIds={sel}
        onSelectionChange={setSel}
        defaultPageSize={10}
        emptyText="No data to display"
      />

      {open && editing && (
        <ReceivedDateDialog
          record={editing}
          lookups={lookups}
          onChange={setEditing}
          onClose={() => setOpen(false)}
          onSave={save}
        />
      )}
    </>
  );
}

function ReceivedDateDialog({ record, lookups, onChange, onClose, onSave }: {
  record: SrReceivedDate;
  lookups: Record<string, SrLookup>;
  onChange: (r: SrReceivedDate) => void; onClose: () => void; onSave: (r: SrReceivedDate) => void;
}) {
  const set = (p: Partial<SrReceivedDate>) => onChange({ ...record, ...p });
  const employeeLk = lookups.employee ?? { cols: ['Name'], rows: [] };
  const relinqLk = lookups.relinq ?? { cols: ['Name'], rows: [] };
  const tatLk = lookups.tat ?? { cols: ['TAT'], rows: [] };
  const matrixLk = lookups.matrix ?? { cols: ['Matrix'], rows: [] };
  const categoryLk = lookups.sampleCategory ?? { cols: ['Sample Category'], rows: [] };
  return (
    <AppDialog open title={`${fmtLocalDateTime(record.date)} – ReceivedDate`} icon="ti-calendar-event" width={820} className="sr-dlg" onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={() => onSave(record)}>OK</button>
      </>}>
      <div className="sr-fgrid">
        <label className="req">Received Date:</label>
        <input type="datetime-local" value={toDateTimeInput(record.date)}
          className={record.date ? undefined : 'sr-req-empty'}
          onChange={e => set({ date: e.target.value })} />
        <label>Relinquished By:</label>
        <LookupCombo lookup={relinqLk} value={record.relinq}
          onPick={r => set({ relinq: r[0] })} popupMatchWidth listMaxHeight={140} />

        <label>Received By:</label>
        <LookupCombo lookup={employeeLk} value={record.by}
          onPick={r => set({ by: r[0] })} popupMatchWidth listMaxHeight={140} />
        <label>TAT:</label>
        <LookupCombo lookup={tatLk} value={record.tat}
          onPick={r => set({ tat: r[0] })} popupMatchWidth listMaxHeight={140} />

        <label>No Of Samples:</label>
        <span className="sr-numspin">
          <button type="button" onClick={() => set({ n: Math.max(0, record.n - 1) })}>−</button>
          <input value={record.n} readOnly aria-label="No Of Samples" />
          <button type="button" onClick={() => set({ n: record.n + 1 })}>＋</button>
        </span>
        <label>Cooler Id:</label>
        <input value={record.cooler} onChange={e => set({ cooler: e.target.value })} />

        <label>Sample Matrix:</label>
        <LookupCombo lookup={matrixLk} multi values={record.matrix}
          onToggle={next => set({ matrix: next })} popupMatchWidth listMaxHeight={140} />
        <label>Created By:</label>
        <input value={record.createdBy} readOnly />

        <label>Sample Category:</label>
        <LookupCombo lookup={categoryLk} multi values={record.cat}
          onToggle={next => set({ cat: next })} popupMatchWidth listMaxHeight={140} />
        <label>Created Date:</label>
        <input value={fmtLocalDate(record.created)} readOnly />
      </div>
    </AppDialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Attachments
// ─────────────────────────────────────────────────────────────────────────────
function AttachmentsTab({
  user, now, tabs, setTabs, onWarn, confirmDelete,
}: RegistrationTabsProps) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<SrAttachment | null>(null);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const catLabel = (cat: string) => {
    const n = Number(cat);
    return Number.isFinite(n) ? (ATT_CAT_LABELS[n] ?? cat) : cat;
  };

  const download = async (r: SrAttachment) => {
    if (!r.fileDataOid) { onWarn('No file attached to this record.'); return; }
    try {
      const res = await api.get(
        `/api/v1/sample-registration/attachments/${r.fileDataOid}/download`,
        { responseType: 'blob' },
      );
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = r.file || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      onWarn('Failed to download the attachment.');
    }
  };

  const cols: AppColumn<SrAttachment>[] = [
    editCol<SrAttachment>(r => { setEditing({ ...r }); setOpen(true); }),
    { field: 'name', header: 'Name', width: 170 },
    { field: 'cat', header: 'Category', width: 110, text: r => catLabel(r.cat) },
    {
      field: 'file', header: 'Attachment', width: 220,
      render: r => r.fileDataOid
        ? <button className="sr-link-btn" onClick={() => download(r)} title="Download">📎 {r.file || 'Download'}</button>
        : <span>📎 {r.file || '—'}</span>,
    },
    { field: 'date', header: 'Date', width: 110, text: r => fmtLocalDate(r.date) },
    { field: 'op', header: 'Operator', width: 110 },
    { field: 'recv', header: 'ReceivedDate', width: 150, text: r => fmtLocalDateTime(r.recv) },
  ];

  const openNew = () => {
    setEditing({
      key: nextKey('att'), oid: '', name: '', cat: String(ATT_CAT_VALUES.COC),
      file: '', fileDataOid: '',
      date: now, op: user, opOid: '', recv: tabs.receivedDates[0]?.date ?? '', comment: '',
    });
    if (fileRef.current) fileRef.current.value = '';
    setOpen(true);
  };

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editing) return;
    const f = e.target.files?.[0];
    if (!f) return;
    setEditing({ ...editing, file: f.name, fileObj: f });
  };

  const uploadFile = async (file: File): Promise<{ fileDataOid: string; fileName: string } | null> => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/api/v1/sample-registration/attachments/upload', fd);
      if (res.data?.success && res.data.data) {
        return { fileDataOid: res.data.data.fileDataOid, fileName: res.data.data.fileName };
      }
      return null;
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      if (msg) onWarn(msg);
      return null;
    }
  };

  const save = async () => {
    if (!editing) return;
    const isNew = !editing.oid;
    if (isNew && !editing.file && !editing.fileObj) {
      onWarn('Browse and select the Attachment file.'); return;
    }
    if (!editing.name.trim()) { onWarn('Enter the Name.'); return; }

    let rec = { ...editing };

    if (rec.fileObj) {
      setUploading(true);
      const result = await uploadFile(rec.fileObj);
      setUploading(false);
      if (!result) { onWarn('File upload failed. Please try again.'); return; }
      rec = { ...rec, fileDataOid: result.fileDataOid, file: result.fileName, fileObj: undefined };
    }

    const exists = tabs.attachments.some(a => a.key === rec.key);
    setTabs({
      ...tabs,
      attachments: exists
        ? tabs.attachments.map(a => (a.key === rec.key ? rec : a))
        : [...tabs.attachments, rec],
    });
    setOpen(false);
  };

  return (
    <>
      <AppToolbar
        onNew={openNew}
        onEdit={() => {
          const rec = tabs.attachments.find(a => sel.has(a.key));
          if (rec) { setEditing({ ...rec }); setOpen(true); }
        }}
        editDisabled={sel.size !== 1}
        onDelete={() => confirmDelete(`${sel.size} attachment(s)`, () => {
          setTabs({ ...tabs, attachments: tabs.attachments.filter(a => !sel.has(a.key)) });
          setSel(new Set());
        })}
        deleteDisabled={sel.size === 0}
      />
      <AppDataGrid<SrAttachment>
        rows={tabs.attachments} columns={cols} getRowId={r => r.key}
        selectable selectedIds={sel} onSelectionChange={setSel}
        defaultPageSize={10} emptyText="No data to display"
      />

      {open && editing && (
        <AppDialog open title="Attachment" icon="ti-paperclip" width={520} className="sr-dlg" onClose={() => setOpen(false)}
          footer={<>
            <button className="ca-btn" onClick={() => setOpen(false)}>Cancel</button>
            <button className="ca-btn ca-btn-solid" disabled={uploading} onClick={save}>
              {uploading ? '⏳ Uploading…' : '💾 OK'}
            </button>
          </>}>
          <div className="sr-fgrid" style={{ gridTemplateColumns: '140px minmax(0, 1fr)' }}>
            <label className="req">Attachment</label>
            <div>
              <input ref={fileRef} type="file" onChange={handleFilePick} />
              {editing.file && !editing.fileObj && editing.fileDataOid && (
                <span style={{ fontSize: 11.5, color: 'var(--ai-muted)' }}>
                  Current: {editing.file}
                </span>
              )}
            </div>
            <label className="req">Name</label>
            <input value={editing.name} className={editing.name ? undefined : 'sr-req-empty'}
              onChange={e => setEditing({ ...editing, name: e.target.value })} />
            <label className="req">Category</label>
            <select value={catLabel(editing.cat)}
              onChange={e => setEditing({ ...editing, cat: String(ATT_CAT_VALUES[e.target.value] ?? 5) })}>
              {ATT_CAT_OPTIONS.map(c => <option key={c}>{c}</option>)}
            </select>
            <label>Received Date</label>
            <select value={editing.recv} onChange={e => setEditing({ ...editing, recv: e.target.value })}>
              <option value="">N/A</option>
              {tabs.receivedDates.map(r => (
                <option key={r.key} value={r.date}>{fmtLocalDateTime(r.date)}</option>
              ))}
            </select>
            <label>Date</label>
            <input value={fmtLocalDate(editing.date)} readOnly />
            <label>Comment</label>
            <input value={editing.comment} onChange={e => setEditing({ ...editing, comment: e.target.value })} />
            <label>Operator</label>
            <input value={editing.op} readOnly />
          </div>
        </AppDialog>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sample Condition Check (SCC) — one per receipt, 17 check points + comments
// ─────────────────────────────────────────────────────────────────────────────
function SccTab({
  header, jobId, user, now, tabs, setTabs, onWarn, confirmDelete,
}: RegistrationTabsProps) {
  const [editing, setEditing] = useState<SrSccRecord | null>(null);
  const [reportOf, setReportOf] = useState<SrSccRecord | null>(null);
  const [sel, setSel] = useState<Set<string | number>>(new Set());

  const blank = (): SrSccRecord => ({
    key: nextKey('scc'), oid: '', matrix: header.matrices[0] ?? 'Water', by: user, date: now,
    recv: tabs.receivedDates[0]?.date ?? now,
    temp: '', corr: '', thermo: '', ph: '', paper: '',
    points: SCC_POINTS.map(p => p[1]),
    descriptions: SCC_POINTS.map(() => ''),
    comments: [],
  });

  const save = (rec: SrSccRecord) => {
    const exists = tabs.sccRecords.some(r => r.key === rec.key);
    setTabs({
      ...tabs,
      sccRecords: exists ? tabs.sccRecords.map(r => (r.key === rec.key ? rec : r)) : [...tabs.sccRecords, rec],
    });
    setEditing(null);
  };

  const cols: AppColumn<SrSccRecord>[] = [
    editCol<SrSccRecord>(r => setEditing({ ...r, points: [...r.points], comments: [...r.comments] })),
    { field: 'matrix', header: 'SampleMatrix', width: 140 },
    { field: 'by', header: 'CheckInBy', width: 120 },
    { field: 'date', header: 'CheckInDate', width: 120, text: r => fmtLocalDate(r.date) },
    { field: 'recv', header: 'ReceivedDate', width: 150, text: r => fmtLocalDateTime(r.recv) },
    { field: 'corr', header: 'CorrectedTemperature', width: 170 },
  ];

  return (
    <>
      <AppToolbar
        left={
          <button className="ca-btn" disabled={sel.size !== 1}
            onClick={() => {
              const rec = tabs.sccRecords.find(r => sel.has(r.key));
              if (rec) setReportOf(rec); else onWarn('Tick an SCC row to preview its report.');
            }}>
            <span className="tb-i tb-i-blue">📄</span> SCC Report
          </button>
        }
        onNew={() => setEditing(blank())}
        onEdit={() => {
          const rec = tabs.sccRecords.find(r => sel.has(r.key));
          if (rec) setEditing({ ...rec, points: [...rec.points], comments: [...rec.comments] });
        }}
        editDisabled={sel.size !== 1}
        onDelete={() => confirmDelete(`${sel.size} SCC record(s)`, () => {
          setTabs({ ...tabs, sccRecords: tabs.sccRecords.filter(r => !sel.has(r.key)) });
          setSel(new Set());
        })}
        deleteDisabled={sel.size === 0}
      />
      <AppDataGrid<SrSccRecord>
        rows={tabs.sccRecords} columns={cols} getRowId={r => r.key}
        selectable selectedIds={sel} onSelectionChange={setSel}
        defaultPageSize={10} emptyText="No SCC yet. Click New to record sample conditions."
      />

      {editing && (
        <SccDialog
          record={editing} header={header} jobId={jobId} user={user}
          receipts={tabs.receivedDates.map(r => r.date)}
          onChange={setEditing}
          onClose={() => setEditing(null)}
          onSave={save}
          onReport={setReportOf}
        />
      )}
      {reportOf && (
        <SccReportDialog record={reportOf} jobId={jobId} client={header.client}
          phone={header.clientPhone} onClose={() => setReportOf(null)} />
      )}
    </>
  );
}

function SccDialog({ record, header, jobId, user, receipts, onChange, onClose, onSave, onReport }: {
  record: SrSccRecord; header: SrHeader; jobId: string; user: string; receipts: string[];
  onChange: (r: SrSccRecord) => void; onClose: () => void;
  onSave: (r: SrSccRecord) => void; onReport: (r: SrSccRecord) => void;
}) {
  const set = (p: Partial<SrSccRecord>) => onChange({ ...record, ...p });
  const [comment, setComment] = useState('');

  const answer = (i: number, v: string) => {
    const points = [...record.points];
    points[i] = points[i] === v ? '' : v;   // Yes / No / N-A are mutually exclusive
    set({ points });
  };

  const addComment = () => {
    const text = comment.trim();
    if (!text) return;
    const c: SrSccComment = { text, user, dt: record.date };
    set({ comments: [...record.comments, c] });
    setComment('');
  };

  return (
    <AppDialog open width={900} icon="ti-clipboard-check" className="sr-dlg"
      title={`${header.client || 'Sample'} - Sample Condition Check`} onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={() => onSave(record)}>
          <span className="tb-i">💾</span> OK
        </button>
      </>}>
      <button className="ca-btn" style={{ marginBottom: 10 }} onClick={() => onReport(record)}>
        <span className="tb-i tb-i-blue">📄</span> SCC Report
      </button>

      <div className="sr-fgrid" style={{ gridTemplateColumns: '150px minmax(0,1fr) 190px minmax(0,1fr)', marginBottom: 12 }}>
        <label>Job ID</label><input value={jobId || 'New'} readOnly />
        <label>Temperature (C)</label>
        <input type="number" step="0.1" value={record.temp} onChange={e => set({ temp: e.target.value })} />

        <label>Client</label><input value={header.client} readOnly />
        <label>Corrected Temperature (C)</label>
        <input type="number" step="0.1" value={record.corr} onChange={e => set({ corr: e.target.value })} />

        <label>Check In By</label><input value={record.by} readOnly />
        <label>Thermometer ID</label>
        <input value={record.thermo} onChange={e => set({ thermo: e.target.value })} />

        <label>Check In Date</label><input value={fmtLocalDate(record.date)} readOnly />
        <label>Sample pH</label>
        <input type="number" step="0.1" value={record.ph} onChange={e => set({ ph: e.target.value })} />

        <label>Sample Matrix</label>
        <select value={record.matrix} onChange={e => set({ matrix: e.target.value })}>
          {(header.matrices.length ? header.matrices : ['Water']).map(m => <option key={m}>{m}</option>)}
        </select>
        <label>pH Paper ID</label>
        <input value={record.paper} onChange={e => set({ paper: e.target.value })} />

        <label>Received Date</label>
        <select value={record.recv} onChange={e => set({ recv: e.target.value })}>
          {receipts.length === 0 && <option value="">N/A</option>}
          {receipts.map(r => <option key={r} value={r}>{fmtLocalDateTime(r)}</option>)}
        </select>
        <span /><span />
      </div>

      <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8 }}>Check Points</div>
      <div style={{ maxHeight: 280, overflow: 'auto', border: '1px solid var(--ai-line)', borderRadius: 4 }}>
        <table className="sr-scc">
          <thead>
            <tr>
              <th>CheckPoint</th>
              <th className="sr-scc-ans">Yes</th>
              <th className="sr-scc-ans">No</th>
              <th className="sr-scc-ans">N/A</th>
              <th style={{ minWidth: 140 }}>Description</th>
            </tr>
          </thead>
          <tbody>
            {SCC_POINTS.map((p, i) => (
              <tr key={p[0]}>
                <td className="sr-scc-pt">{p[0]}</td>
                {(['yes', 'no', 'na'] as const).map(v => (
                  <td key={v} className="sr-scc-ans">
                    <input type="checkbox" checked={record.points[i] === v}
                      aria-label={`${p[0]} — ${v}`}
                      onChange={() => answer(i, v)} />
                  </td>
                ))}
                <td>
                  <input
                    style={{ width: '100%', border: '1px solid var(--ai-line)', borderRadius: 3, padding: '2px 6px', fontSize: 12 }}
                    value={record.descriptions[i] ?? ''}
                    aria-label={`${p[0]} description`}
                    onChange={e => {
                      const descriptions = [...record.descriptions];
                      descriptions[i] = e.target.value;
                      set({ descriptions });
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ fontWeight: 800, fontSize: 13, margin: '12px 0 8px' }}>Comment</div>
      <div className="sr-subtoolbar" style={{ paddingTop: 0 }}>
        <input style={{ flex: 1, minWidth: 200, border: '1px solid #b9c4d0', borderRadius: 3, padding: '4px 8px', fontSize: 12.5 }}
          placeholder="Note anything wrong or any violation…"
          value={comment}
          onChange={e => setComment(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addComment(); }} />
        <button className="ca-btn" onClick={addComment}><span className="tb-i tb-i-green">＋</span> New</button>
      </div>
      <table className="sr-scc">
        <thead><tr><th>Comment</th><th style={{ width: 130 }}>User</th><th style={{ width: 160 }}>DateTime</th></tr></thead>
        <tbody>
          {record.comments.length === 0 ? (
            <tr><td colSpan={3} style={{ textAlign: 'center', fontStyle: 'italic', color: '#9aa1ad', padding: 16 }}>
              No data to display
            </td></tr>
          ) : record.comments.map((c, i) => (
            <tr key={i}><td className="sr-scc-pt">{c.text}</td><td>{c.user}</td><td>{fmtLocalDateTime(c.dt)}</td></tr>
          ))}
        </tbody>
      </table>
    </AppDialog>
  );
}

/** The printed "Sample Condition Checklist" — letterhead, bordered header
 *  table, numbered check-point rows with ✔ marks, comments box, page footer.
 *  TODO(AI-29131): render through the shared IPdfReportGenerator<T> once the
 *  SCC report provider exists (standing rule 8) — this is the on-screen preview. */
function SccReportDialog({ record, jobId, client, phone, onClose }: {
  record: SrSccRecord; jobId: string; client: string; phone: string; onClose: () => void;
}) {
  const bd: React.CSSProperties = {
    border: '1px solid #444', padding: '4px 8px', fontSize: 12.5, background: '#fff', color: '#111',
  };
  const mark: React.CSSProperties = { ...bd, fontWeight: 900, fontSize: 14, textAlign: 'center' };
  return (
    <AppDialog open width={860} icon="ti-file-text" className="sr-dlg" title={`SCC Report — ${jobId || 'New'}`}
      onClose={onClose}
      footer={<button className="ca-btn" onClick={onClose}>Close</button>}>
      <div style={{ background: '#fff', border: '1px solid #d8dce2', padding: '26px 30px', color: '#111' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 4 }}>
          <div style={{ flex: 1, textAlign: 'center', fontWeight: 800, fontSize: 21, paddingTop: 4 }}>
            Sample Condition Checklist
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 12.5, marginBottom: 6 }}>
          <b>Date :</b>&nbsp;&nbsp;{fmtLocalDate(record.date)}
        </div>
        <div style={{ borderTop: '3px solid #000' }} />
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
          <tbody>
            {([
              ['Job ID :', jobId || 'New', 'Temperature:', record.temp],
              ['Client:', client, 'Thermometer ID:', record.thermo],
              ['Check In By :', record.by, 'Sample pH:', record.ph],
              ['Check In Date :', fmtLocalDateTime(record.date), 'pH Paper ID:', record.paper],
              ['Sample Matrix:', record.matrix, 'Received Date:', fmtLocalDateTime(record.recv)],
            ] as [string, string, string, string][]).map(([l1, v1, l2, v2]) => (
              <tr key={l1}>
                <td style={{ ...bd, width: 130 }}><b>{l1}</b></td>
                <td style={{ ...bd, width: '32%' }}>{v1}</td>
                <td style={{ ...bd, width: 150 }}><b>{l2}</b></td>
                <td style={bd}>{v2}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...bd, width: 44, textAlign: 'center' }}>S.No</th>
              <th style={{ ...bd, textAlign: 'center' }}>Check Points</th>
              <th style={{ ...bd, width: 52, textAlign: 'center' }}>Yes</th>
              <th style={{ ...bd, width: 52, textAlign: 'center' }}>No</th>
              <th style={{ ...bd, width: 52, textAlign: 'center' }}>N/A</th>
            </tr>
          </thead>
          <tbody>
            {SCC_POINTS.map((p, i) => (
              <tr key={p[0]}>
                <td style={{ ...bd, textAlign: 'center' }}>{i + 1}</td>
                <td style={bd}>{p[0]}</td>
                <td style={mark}>{record.points[i] === 'yes' ? '✔' : ''}</td>
                <td style={mark}>{record.points[i] === 'no' ? '✔' : ''}</td>
                <td style={mark}>{record.points[i] === 'na' ? '✔' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr><td style={{ ...bd, fontWeight: 700 }}>Comments :</td></tr>
            <tr>
              <td style={{ ...bd, height: 110, verticalAlign: 'top' }}>
                {record.comments.map((c, i) => (
                  <div key={i}>{c.text} — {c.user}, {fmtLocalDateTime(c.dt)}</div>
                ))}
              </td>
            </tr>
          </tbody>
        </table>
        <div style={{ display: 'flex', marginTop: 26, fontSize: 12.5, borderBottom: '3px solid #000', paddingBottom: 6 }}>
          <div style={{ flex: 1 }}><b>Phone :</b>&nbsp;&nbsp;{phone || '—'}</div>
          <div style={{ flex: 1, textAlign: 'center' }}>Page 1 of 1</div>
          <div style={{ flex: 1 }} />
        </div>
      </div>
    </AppDialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Custom Request — per-test Hold, Default Due Date, Due Date, TAT override
// Matches legacy XAF: Matrix | Test | Method | Hold | DefaultDueDate | DueDate | Parameter | TAT
// ─────────────────────────────────────────────────────────────────────────────
function CustomRequestTab({
  header, templateTests, customRequest, setCustomRequest,
  tatHoursMap, holidaySet, registrationType,
}: RegistrationTabsProps) {
  interface CrRow { name: string; method: string; matrix: string }

  // Matrix column shows the job's selected matrix (same for all tests — single
  // matrix per test template picker call). Multiple matrices show semicolon-joined.
  const matrixDisplay = header.matrices.join('; ');

  const rows: CrRow[] = useMemo(
    () => templateTests.map(t => ({ name: t.name, method: t.method, matrix: matrixDisplay })),
    [templateTests, matrixDisplay],
  );

  const tatNames = useMemo(() => Object.keys(tatHoursMap).sort(), [tatHoursMap]);
  const isSampling = registrationType === 'Sampling';

  const entry = (name: string) =>
    customRequest[name] ?? { hold: false, due: '', tat: header.tat };

  const patchCr = (name: string, p: Partial<{ hold: boolean; due: string; tat: string }>) =>
    setCustomRequest({ ...customRequest, [name]: { ...entry(name), ...p } });

  const handleTatChange = (name: string, newTat: string) => {
    const hours = tatHoursMap[newTat];
    const newDue = hours !== undefined
      ? toDateInput(resolveDueDate(header.receivedDate, hours, isSampling, holidaySet))
      : entry(name).due;
    patchCr(name, { tat: newTat, due: newDue });
  };

  const cols: AppColumn<CrRow>[] = [
    { field: 'matrix', header: 'Matrix', width: 130 },
    { field: 'name', header: 'Test', width: 220 },
    { field: 'method', header: 'Method', width: 150 },
    {
      field: 'hold', header: 'Hold', width: 60, align: 'center', sortable: false, filterable: false,
      render: r => (
        <input type="checkbox" checked={entry(r.name).hold} aria-label={`Hold ${r.name}`}
          onChange={e => patchCr(r.name, { hold: e.target.checked })} />
      ),
    },
    {
      field: 'defaultDue', header: 'Default Due Date', width: 140,
      text: () => fmtLocalDate(header.dueDate),
    },
    {
      field: 'due', header: 'Due Date', width: 150, sortable: false, filterable: false,
      render: r => (
        <input type="date" value={entry(r.name).due || toDateInput(header.dueDate)}
          aria-label={`Due date for ${r.name}`}
          style={{ border: '1px solid var(--ai-line)', borderRadius: 3, padding: '1px 4px', fontSize: 12 }}
          onChange={e => patchCr(r.name, { due: e.target.value })} />
      ),
    },
    {
      field: 'param', header: 'Parameter', width: 100,
      text: () => 'AllParam',
    },
    {
      field: 'tat', header: 'TAT', width: 140, sortable: false, filterable: false,
      render: r => (
        <select
          value={entry(r.name).tat || header.tat}
          aria-label={`TAT for ${r.name}`}
          style={{ border: '1px solid var(--ai-line)', borderRadius: 3, padding: '1px 4px', fontSize: 12, width: '100%' }}
          onChange={e => handleTatChange(r.name, e.target.value)}
        >
          {!entry(r.name).tat && !header.tat && <option value="">— Select TAT —</option>}
          {tatNames.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      ),
    },
  ];

  return (
    <>
      <AppDataGrid<CrRow>
        rows={rows} columns={cols} getRowId={r => r.name}
        defaultPageSize={20}
        emptyText="Pick a Sample Matrix and tests first."
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Notes — author-only edit / delete (§14a Rule C)
// Matches XAF ProjectNotesViewController: two-level popup (list → detail),
// author-only enforcement on edit AND delete, rich text body, Sort field.
// ─────────────────────────────────────────────────────────────────────────────

/** Strip HTML tags → plain text (for grid preview + PlainText column). */
function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Build a blank SrNote for "New". */
function blankNote(user: string, now: string, jobId: string): SrNote {
  return {
    key: nextKey('note'), oid: '', title: '', author: user, authorOid: '',
    date: now, sort: 0, html: '', plainText: '',
    sourceId: jobId, noteSource: 'Sample Registration',
  };
}

function NotesTab({ user, now, tabs, setTabs, jobId, onWarn, confirmDelete }: RegistrationTabsProps) {
  const [editing, setEditing] = useState<SrNote | null>(null);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const bodyRef = useRef<HTMLDivElement>(null);

  const cols: AppColumn<SrNote>[] = [
    editCol<SrNote>(r => {
      if (notAuthor(r)) { onWarn('You are not authorized to modify a note created by another author.'); return; }
      setEditing({ ...r });
    }),
    { field: 'title', header: 'Title', width: 180 },
    { field: 'plainText', header: 'Note', width: 320, text: r => r.plainText || stripHtml(r.html) },
    { field: 'author', header: 'Author', width: 120 },
    { field: 'date', header: 'Date', width: 150, text: r => fmtLocalDateTime(r.date) },
    { field: 'sort', header: 'Sort', type: 'number', width: 70 },
  ];

  const save = () => {
    if (!editing) return;
    if (!editing.title.trim()) { onWarn('Enter the Title.'); return; }
    const html = bodyRef.current?.innerHTML ?? editing.html;
    const plain = stripHtml(html);
    if (!plain) { onWarn('Enter the Note text.'); return; }
    const rec: SrNote = { ...editing, html, plainText: plain };
    const exists = tabs.notes.some(n => n.key === rec.key);
    setTabs({ ...tabs, notes: exists ? tabs.notes.map(n => (n.key === rec.key ? rec : n)) : [...tabs.notes, rec] });
    setEditing(null);
  };

  /** §14a Rule C — a note may be edited or deleted ONLY by its author. */
  const notAuthor = (n: SrNote) => n.author !== user;

  const tryEdit = () => {
    const n = tabs.notes.find(x => sel.has(x.key));
    if (!n) return;
    if (notAuthor(n)) { onWarn('You are not authorized to modify a note created by another author.'); return; }
    setEditing({ ...n });
  };

  const tryDelete = () => {
    const picked = tabs.notes.filter(n => sel.has(n.key));
    if (picked.some(notAuthor)) {
      onWarn('You are not authorized to modify a note created by another author.');
      return;
    }
    confirmDelete(`${sel.size} note(s)`, () => {
      setTabs({ ...tabs, notes: tabs.notes.filter(n => !sel.has(n.key)) });
      setSel(new Set());
    });
  };

  return (
    <>
      <AppToolbar
        onNew={() => setEditing(blankNote(user, now, jobId))}
        onEdit={tryEdit}
        editDisabled={sel.size !== 1}
        onDelete={tryDelete}
        deleteDisabled={sel.size === 0}
      />
      <AppDataGrid<SrNote>
        rows={tabs.notes} columns={cols} getRowId={r => r.key}
        selectable selectedIds={sel} onSelectionChange={setSel}
        defaultPageSize={10} emptyText="No data to display"
      />

      {editing && (
        <AppDialog open title="Note"
          icon="ti-note" width={750} className="sr-dlg" onClose={() => setEditing(null)}
          footer={<>
            <button className="ca-btn" onClick={() => setEditing(null)}>Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={save}>OK</button>
          </>}>
          <div className="sr-note-grid">
            <label className="req">Title:</label>
            <div className="sr-note-val">
              {!editing.title.trim() && <span className="sr-val-icon" title="Required">⊘</span>}
              <input value={editing.title} className={editing.title.trim() ? undefined : 'sr-req-empty'}
                onChange={e => setEditing({ ...editing, title: e.target.value })} />
            </div>
            <label>Business Object:</label>
            <input value={editing.noteSource || 'Sample Registration'} readOnly />

            <label>Source ID:</label>
            <input value={editing.sourceId || jobId} readOnly />
            <label>Author:</label>
            <input value={editing.author} readOnly />

            <label>Name Source:</label>
            <input value={editing.noteSource || 'Sample Registration'} readOnly />
            <label>Date:</label>
            <input value={fmtLocalDateTime(editing.date)} readOnly />
          </div>
          <div className="sr-rte-wrap">
            <span className="sr-rte-tab">Note</span>
            <div className="sr-rte">
              <div className="sr-rte-bar">
                <button className="sr-rte-btn" onClick={() => document.execCommand('undo')} title="Undo"><i className="ti ti-arrow-back-up" /></button>
                <button className="sr-rte-btn" onClick={() => document.execCommand('redo')} title="Redo"><i className="ti ti-arrow-forward-up" /></button>
                <span className="sr-rte-sep" />
                <button className="sr-rte-btn" onClick={() => document.execCommand('copy')} title="Copy"><i className="ti ti-copy" /></button>
                <button className="sr-rte-btn" onClick={() => document.execCommand('cut')} title="Cut"><i className="ti ti-cut" /></button>
                <button className="sr-rte-btn" onClick={() => document.execCommand('paste')} title="Paste"><i className="ti ti-clipboard" /></button>
                <span className="sr-rte-sep" />
                <button className="sr-rte-dd sr-rte-font" onClick={() => {}}>Calibri</button>
                <button className="sr-rte-dd" onClick={() => document.execCommand('bold')}>Font</button>
                <button className="sr-rte-dd" onClick={() => {}}>Paragraph</button>
                <button className="sr-rte-dd" onClick={() => {}}>Styles</button>
                <button className="sr-rte-dd" onClick={() => {}}>Editing</button>
              </div>
              <div ref={bodyRef} className="sr-rte-body" contentEditable suppressContentEditableWarning
                dangerouslySetInnerHTML={{ __html: editing.html }} />
            </div>
          </div>
        </AppDialog>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Item Charge — extra line items that roll into the invoice
// ─────────────────────────────────────────────────────────────────────────────
/** Draft state shared between Add and Edit dialogs in ItemChargeTab. */
interface IcDraft {
  /** '' = Add mode; row.key = Edit mode. */
  key: string;
  /** Persisted Oid — '' for new rows. */
  oid: string;
  itemPriceOid: string;
  qty: number;
  disc: number;
  desc: string;
  remark: string;
}

function ItemChargeTab({ tabs, setTabs, confirmDelete }: RegistrationTabsProps) {
  const [pricing, setPricing] = useState<IcPricingOption[]>([]);
  const [draft, setDraft] = useState<IcDraft | null>(null);
  const [sel, setSel] = useState<Set<string | number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    api.get('/api/v1/sample-registration/item-charge-pricing')
      .then(res => {
        if (!cancelled && res.data?.data) {
          const items: IcPricingOption[] = res.data.data;
          setPricing(items);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const money = (n: number) => `$${n.toFixed(2)}`;

  const openAdd = () => setDraft({
    key: '', oid: '', itemPriceOid: pricing[0]?.oid ?? '',
    qty: 1, disc: 0, desc: '', remark: '',
  });

  const openEdit = (r: SrItemCharge) => setDraft({
    key: r.key, oid: r.oid,
    itemPriceOid: r.itemPriceOid || pricing.find(p => p.itemName === r.name)?.oid || pricing[0]?.oid || '',
    qty: r.qty, disc: r.disc, desc: r.desc, remark: r.remark,
  });

  const save = () => {
    if (!draft) return;
    const it = pricing.find(p => p.oid === draft.itemPriceOid);
    if (!it) return;
    const amount = it.unitPrice * draft.qty;
    const row: SrItemCharge = {
      key: draft.key || nextKey('ic'),
      oid: draft.oid,
      itemPriceOid: it.oid,
      name: it.itemName,
      desc: draft.desc.trim(),
      price: it.unitPrice,
      qty: draft.qty,
      amount,
      final: amount * (1 - draft.disc / 100),
      npPrice: it.npUnitPrice,
      disc: draft.disc,
      remark: draft.remark,
    };
    if (draft.key) {
      setTabs({ ...tabs, itemCharges: tabs.itemCharges.map(c => c.key === draft.key ? row : c) });
    } else {
      setTabs({ ...tabs, itemCharges: [...tabs.itemCharges, row] });
    }
    setDraft(null);
    setSel(new Set());
  };

  const tryEdit = () => {
    const r = tabs.itemCharges.find(x => sel.has(x.key));
    if (r) openEdit(r);
  };

  const cols: AppColumn<SrItemCharge>[] = [
    editCol<SrItemCharge>(openEdit),
    { field: 'name', header: 'ItemName', width: 180 },
    { field: 'desc', header: 'Description', width: 200 },
    { field: 'qty', header: 'Qty', type: 'number', width: 70 },
    { field: 'price', header: 'UnitPrice', type: 'number', width: 100, text: r => money(r.price) },
    { field: 'amount', header: 'Amount', type: 'number', width: 100, text: r => money(r.amount) },
    { field: 'disc', header: 'Discount(%)', type: 'number', width: 110 },
    { field: 'final', header: 'FinalAmount', type: 'number', width: 120, text: r => money(r.final) },
    { field: 'remark', header: 'Remark', width: 150 },
  ];

  const isEditing = !!draft?.key;

  return (
    <>
      <div className="sr-subtoolbar">
        <button className="ca-btn" onClick={openAdd} disabled={pricing.length === 0}>
          <span className="tb-i tb-i-green">＋</span> Add
        </button>
        <button className="ca-btn" disabled={sel.size !== 1} onClick={tryEdit}>
          <span className="tb-i tb-i-blue">✎</span> Edit
        </button>
        <button className="ca-btn" disabled={sel.size === 0}
          onClick={() => confirmDelete(`${sel.size} item charge(s)`, () => {
            setTabs({ ...tabs, itemCharges: tabs.itemCharges.filter(c => !sel.has(c.key)) });
            setSel(new Set());
          })}>
          <span className="tb-i tb-i-red">－</span> Remove
        </button>
      </div>
      <AppDataGrid<SrItemCharge>
        rows={tabs.itemCharges} columns={cols} getRowId={r => r.key}
        selectable selectedIds={sel} onSelectionChange={setSel}
        defaultPageSize={10} emptyText="No data to display"
      />

      {draft !== null && (
        <AppDialog open title={isEditing ? 'Edit Item Charge' : 'Item Charge'} icon="ti-receipt"
          width={520} className="sr-dlg" onClose={() => setDraft(null)}
          footer={<>
            <button className="ca-btn" onClick={() => setDraft(null)}>Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={save} disabled={!draft.itemPriceOid}>
              <span className="tb-i">💾</span> OK
            </button>
          </>}>
          <div className="sr-fgrid" style={{ gridTemplateColumns: '120px minmax(0,1fr)' }}>
            <label className="req">Item</label>
            <select value={draft.itemPriceOid}
              onChange={e => setDraft({ ...draft, itemPriceOid: e.target.value })}>
              {pricing.map(it => (
                <option key={it.oid} value={it.oid}>
                  {it.itemCode} — {it.itemName} ({money(it.unitPrice)})
                </option>
              ))}
            </select>
            <label>Qty</label>
            <span className="sr-numspin">
              <button type="button" onClick={() => setDraft({ ...draft, qty: Math.max(1, draft.qty - 1) })}>−</button>
              <input value={draft.qty} readOnly aria-label="Qty" />
              <button type="button" onClick={() => setDraft({ ...draft, qty: draft.qty + 1 })}>＋</button>
            </span>
            <label>Discount (%)</label>
            <span className="sr-numspin">
              <button type="button" onClick={() => setDraft({ ...draft, disc: Math.max(0, draft.disc - 5) })}>−</button>
              <input value={draft.disc} readOnly aria-label="Discount" />
              <button type="button" onClick={() => setDraft({ ...draft, disc: Math.min(100, draft.disc + 5) })}>＋</button>
            </span>
            <label>Description</label>
            <input value={draft.desc}
              onChange={e => setDraft({ ...draft, desc: e.target.value })} />
            <label>Remark</label>
            <input value={draft.remark}
              onChange={e => setDraft({ ...draft, remark: e.target.value })} />
          </div>
        </AppDialog>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Image Edit
// ─────────────────────────────────────────────────────────────────────────────
function ImageEditTab({ tabs, setTabs, onWarn, confirmDelete }: RegistrationTabsProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<SrImage>({ key: '', oid: '', name: '', photo: '', sort: 0, comment: '' });
  const [preview, setPreview] = useState<SrImage | null>(null);
  const [sel, setSel] = useState<Set<string | number>>(new Set());

  const cols: AppColumn<SrImage>[] = [
    { field: 'name', header: 'Name', width: 180 },
    { field: 'photo', header: 'SamplePhoto', width: 220, render: r => <span>🖼 {r.photo || '—'}</span> },
    { field: 'sort', header: 'Sort', type: 'number', width: 70 },
    { field: 'comment', header: 'Comment', width: 260 },
  ];

  const add = () => {
    if (!draft.name.trim()) { onWarn('Enter the Name.'); return; }
    setTabs({ ...tabs, images: [...tabs.images, { ...draft, oid: '', key: nextKey('img') }] });
    setOpen(false);
  };

  return (
    <>
      <div className="sr-subtoolbar">
        <button className="ca-btn"
          onClick={() => { setDraft({ key: '', oid: '', name: '', photo: '', sort: 0, comment: '' }); setOpen(true); }}>
          <span className="tb-i tb-i-green">＋</span> New
        </button>
        <button className="ca-btn" disabled={sel.size === 0}
          onClick={() => confirmDelete(`${sel.size} image(s)`, () => {
            setTabs({ ...tabs, images: tabs.images.filter(m => !sel.has(m.key)) });
            setSel(new Set());
          })}>
          <span className="tb-i tb-i-red">🔗</span> Unlink
        </button>
        <button className="ca-btn" disabled={sel.size !== 1}
          onClick={() => {
            const m = tabs.images.find(x => sel.has(x.key));
            if (m) setPreview(m);
          }}>
          <span className="tb-i tb-i-blue">👁</span> Preview
        </button>
      </div>
      <AppDataGrid<SrImage>
        rows={tabs.images} columns={cols} getRowId={r => r.key}
        selectable selectedIds={sel} onSelectionChange={setSel}
        defaultPageSize={10} emptyText="No data to display"
      />

      {open && (
        <AppDialog open title="Image" icon="ti-photo" width={520} className="sr-dlg" onClose={() => setOpen(false)}
          footer={<>
            <button className="ca-btn" onClick={() => setOpen(false)}>Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={add}><span className="tb-i">💾</span> OK</button>
          </>}>
          <div className="sr-fgrid" style={{ gridTemplateColumns: '120px minmax(0,1fr)' }}>
            <label className="req">Name</label>
            <input value={draft.name} className={draft.name ? undefined : 'sr-req-empty'}
              onChange={e => setDraft({ ...draft, name: e.target.value })} />
            <label>SamplePhoto</label>
            <input type="file" accept="image/*"
              onChange={e => setDraft({ ...draft, photo: e.target.files?.[0]?.name ?? '' })} />
            <label>Sort</label>
            <span className="sr-numspin">
              <button type="button" onClick={() => setDraft(d => ({ ...d, sort: Math.max(0, d.sort - 1) }))}>−</button>
              <input value={draft.sort} readOnly aria-label="Sort" />
              <button type="button" onClick={() => setDraft(d => ({ ...d, sort: d.sort + 1 }))}>＋</button>
            </span>
            <label>Comment</label>
            <input value={draft.comment} onChange={e => setDraft({ ...draft, comment: e.target.value })} />
          </div>
        </AppDialog>
      )}

      {preview && (
        <AppDialog open title={`Preview — ${preview.name}`} icon="ti-photo" width={520} className="sr-dlg"
          onClose={() => setPreview(null)}
          footer={<button className="ca-btn" onClick={() => setPreview(null)}>Close</button>}>
          <div style={{ textAlign: 'center', padding: 30, background: 'var(--ai-readonly-bg)', border: '1px dashed #b9c4d0', borderRadius: 6 }}>
            <div style={{ fontSize: 52 }}>🖼</div>
            <div style={{ fontWeight: 700, marginTop: 8 }}>{preview.photo || '(no photo attached)'}</div>
            <div className="sr-hint-inline">{preview.comment}</div>
          </div>
        </AppDialog>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared column helper
// ─────────────────────────────────────────────────────────────────────────────
/** The narrow row-edit pencil column every tab grid starts with. */
function editCol<T>(onEdit: (row: T) => void): AppColumn<T> {
  return {
    field: '__edit', header: '', width: 34, align: 'center',
    sortable: false, filterable: false, noExport: true,
    render: row => (
      <button className="sr-edit-cell" title="Edit"
        onClick={e => { e.stopPropagation(); onEdit(row); }}>✎</button>
    ),
  };
}
